import { useRef, useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Close as CloseIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  StrikethroughS as StrikethroughSIcon,
  Link as LinkIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatIndentIncrease as FormatIndentIncreaseIcon,
  FormatIndentDecrease as FormatIndentDecreaseIcon,
  FormatQuote as FormatQuoteIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as SheetIcon,
  Slideshow as SlideIcon,
  InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material'
import {
  orgEmailFilesApi,
  OrgEmailFile,
  formatBytes,
} from '../../lib/api/orgEmailFilesApi'
import axiosInstance from '../../lib/api/axiosInstance'
import { authStore } from '../../lib/stores/authStore'
import { themeColors } from '../../app/layout/theme'
import { toast } from 'react-toastify'

export interface SendEmailData {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body: string
  attachments: File[]
}

export interface SendEmailDrawerProps {
  open: boolean
  onClose: () => void
  senderEmail?: string
  defaultTo?: string[]
  onSend?: (data: SendEmailData) => void
  isSending?: boolean
  /** orgId of the entity being emailed (lead / deal / contact).
   *  Used to scope pre-uploaded files to the correct organisation.
   *  Falls back to the logged-in user's orgId when not provided. */
  orgId?: string | null
}

// ── pre-uploaded file type icon ───────────────────────────────────────────────
function PreFileIcon({ type }: { type: string }) {
  const sx = { fontSize: 16 }
  if (type.startsWith('image/'))                               return <ImageIcon sx={{ ...sx, color: '#10b981' }} />
  if (type === 'application/pdf')                              return <PdfIcon   sx={{ ...sx, color: '#ef4444' }} />
  if (type.includes('word') || type.includes('document'))      return <DocIcon   sx={{ ...sx, color: '#3b82f6' }} />
  if (type.includes('sheet') || type.includes('excel'))        return <SheetIcon sx={{ ...sx, color: '#16a34a' }} />
  if (type.includes('presentation') || type.includes('power')) return <SlideIcon sx={{ ...sx, color: '#f97316' }} />
  return <AttachFileIcon sx={{ ...sx, color: '#9ca3af' }} />
}

const toolbarBtnSx = {
  borderRadius: '4px',
  p: '4px',
  '&:hover': { bgcolor: '#f0f0f0' },
}

function activeToolbarBtnSx(isActive: boolean) {
  return {
    ...toolbarBtnSx,
    ...(isActive ? { bgcolor: '#e8f0fe', color: '#1a73e8', '&:hover': { bgcolor: '#d2e3fc' } } : {}),
  }
}

const TOOLBAR_FMT = [
  { label: 'Bold',          icon: <FormatBoldIcon sx={{ fontSize: 17 }} />,          cmd: 'bold' },
  { label: 'Italic',        icon: <FormatItalicIcon sx={{ fontSize: 17 }} />,        cmd: 'italic' },
  { label: 'Underline',     icon: <FormatUnderlinedIcon sx={{ fontSize: 17 }} />,    cmd: 'underline' },
  { label: 'Strikethrough', icon: <StrikethroughSIcon sx={{ fontSize: 17 }} />,      cmd: 'strikeThrough' },
]
const TOOLBAR_LIST = [
  { label: 'Bulleted List',  icon: <FormatListBulletedIcon sx={{ fontSize: 17 }} />, cmd: 'insertUnorderedList' },
  { label: 'Numbered List',  icon: <FormatListNumberedIcon sx={{ fontSize: 17 }} />, cmd: 'insertOrderedList' },
]
const TOOLBAR_ALIGN = [
  { label: 'Align Left',   icon: <FormatAlignLeftIcon sx={{ fontSize: 17 }} />,   cmd: 'justifyLeft' },
  { label: 'Align Center', icon: <FormatAlignCenterIcon sx={{ fontSize: 17 }} />, cmd: 'justifyCenter' },
  { label: 'Align Right',  icon: <FormatAlignRightIcon sx={{ fontSize: 17 }} />,  cmd: 'justifyRight' },
]
const TOOLBAR_INDENT = [
  { label: 'Indent',  icon: <FormatIndentIncreaseIcon sx={{ fontSize: 17 }} />, cmd: 'indent' },
  { label: 'Outdent', icon: <FormatIndentDecreaseIcon sx={{ fontSize: 17 }} />, cmd: 'outdent' },
]

export function SendEmailDrawer({
  open,
  onClose,
  senderEmail = 'assignedstaff@company.com',
  defaultTo = [],
  onSend,
  isSending = false,
  orgId: entityOrgId,
}: SendEmailDrawerProps) {
  const [toEmails,  setToEmails]  = useState<string[]>(defaultTo)
  const [toInput,   setToInput]   = useState('')
  const [ccEmails,  setCcEmails]  = useState<string[]>([])
  const [ccInput,   setCcInput]   = useState('')
  const [bccEmails, setBccEmails] = useState<string[]>([])
  const [bccInput,  setBccInput]  = useState('')
  const [showCc,    setShowCc]    = useState(false)
  const [showBcc,   setShowBcc]   = useState(false)
  const [subject,   setSubject]   = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>({})
  const bodyRef     = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // pre-uploaded files popover
  const [filesAnchor, setFilesAnchor] = useState<HTMLElement | null>(null)
  const closeFilesPopover = () => setFilesAnchor(null)

  // Resolve which orgId to scope pre-uploaded files to
  const resolvedOrgId = entityOrgId || authStore.user?.orgId || null

  // Fetch pre-uploaded files from the API — scoped to the entity's org
  const { data: preFiles = [], isFetching: preFilesLoading } = useQuery<OrgEmailFile[]>({
    queryKey: ['org-email-files-drawer', resolvedOrgId ?? 'all'],
    queryFn:  () => orgEmailFilesApi.getAll(resolvedOrgId),
    enabled:  Boolean(filesAnchor), // only fetch when popover is open
    staleTime: 30_000,
  })

  const openFilesPopover = (e: React.MouseEvent<HTMLElement>) => {
    setFilesAnchor(e.currentTarget)
  }

  const attachPreFile = useCallback(async (stored: OrgEmailFile) => {
    // avoid duplicates by name
    if (attachments.some(a => a.name === stored.fileName)) {
      closeFilesPopover()
      return
    }
    try {
      // Fetch the file bytes via backend API proxy to bypass CORS
      const res = await axiosInstance.get(`/org-email-files/${stored.id}/download`, {
        responseType: 'blob'
      })
      const blob = res.data
      const file = new File([blob], stored.fileName, { type: stored.contentType })
      setAttachments(prev => [...prev, file])
    } catch (e) {
     
      toast.error(`Failed to attach "${stored.fileName}"`)
    }
    closeFilesPopover()
  }, [attachments])

  useEffect(() => {
    if (open) {
      if (defaultTo.length === 0) {
        toast.error('No email address found for this contact')
        onClose()
        return
      }
      // Sync recipient list each time the drawer opens
      setToEmails(defaultTo)
      setCcEmails([])
      setBccEmails([])
      setSubject('')
      setAttachments([])
      if (bodyRef.current) bodyRef.current.innerHTML = ''
    }
  }, [open])

  const addEmail = (
    raw: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
  ) => {
    const email = raw.trim()
    if (email && !list.includes(email)) setList([...list, email])
    setInput('')
  }

  const handleKeyDown = (
    e: React.KeyboardEvent,
    input: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(input, list, setList, setInput)
    }
  }

  const updateActiveFormats = useCallback(() => {
    let isBlockquote = false
    try {
      const formatBlockVal = document.queryCommandValue('formatBlock')
      isBlockquote = typeof formatBlockVal === 'string' && formatBlockVal.toLowerCase() === 'blockquote'
    } catch (e) {
      // queryCommandValue can throw in some edge cases
    }

    setActiveFormats({
      bold:          document.queryCommandState('bold'),
      italic:        document.queryCommandState('italic'),
      underline:     document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList:   document.queryCommandState('insertOrderedList'),
      justifyLeft:   document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight:  document.queryCommandState('justifyRight'),
      blockquote:    isBlockquote,
    })
  }, [])

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    bodyRef.current?.focus()
    updateActiveFormats()
  }

  const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setAttachments(prev => [...prev, ...Array.from(e.target.files!)])
    e.target.value = ''
  }

  const handleSend = () => {
    onSend?.({
      to: toEmails,
      cc: ccEmails,
      bcc: bccEmails,
      subject,
      body: bodyRef.current?.innerHTML ?? '',
      attachments,
    })
  }

  const handleClose = () => {
    setToEmails(defaultTo)
    setToInput('')
    setCcEmails([])
    setCcInput('')
    setBccEmails([])
    setBccInput('')
    setShowCc(false)
    setShowBcc(false)
    setSubject('')
    setAttachments([])
    if (bodyRef.current) bodyRef.current.innerHTML = ''
    onClose()
  }

  const canSend = toEmails.length > 0 && subject.trim().length > 0

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 620 },
          maxWidth: '100vw',
          bgcolor: '#fff',
          boxShadow: themeColors.shadow?.dialog ?? '0 8px 32px rgba(0,0,0,0.18)',
        },
      }}
      ModalProps={{ keepMounted: true }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* ── Header ── */}
        <Box sx={{ height: 56, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: themeColors.text.primary }}>
            Send Email
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isSending}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Divider />

        {/* ── Scrollable Body ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

          {/* Sender */}
          <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${themeColors.border}` }}>
            <Typography sx={{ fontSize: 13, color: themeColors.text.secondary, width: 56, flexShrink: 0 }}>
              Sender
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: '#e8820c', fontWeight: 700 }}>
                {senderEmail[0]?.toUpperCase()}
              </Avatar>
              <Typography sx={{ fontSize: 13, color: themeColors.text.primary }}>{senderEmail}</Typography>
            </Box>
          </Box>

          {/* To */}
          <RecipientRow
            label="To*"
            emails={toEmails}
            input={toInput}
            onInputChange={setToInput}
            onAdd={() => addEmail(toInput, toEmails, setToEmails, setToInput)}
            onRemove={e => setToEmails(toEmails.filter(x => x !== e))}
            onKeyDown={e => handleKeyDown(e, toInput, toEmails, setToEmails, setToInput)}
            placeholder={toEmails.length === 0 ? 'Add email address' : ''}
            actions={
              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0, alignSelf: 'flex-start', pt: 0.5 }}>
                <Button size="small" onClick={() => setShowCc(!showCc)}
                  sx={{ fontSize: 11, textTransform: 'none', color: showCc ? themeColors.primary : themeColors.text.secondary, minWidth: 0, px: 0.75, py: 0.25, fontWeight: 600 }}>
                  CC
                </Button>
                <Button size="small" onClick={() => setShowBcc(!showBcc)}
                  sx={{ fontSize: 11, textTransform: 'none', color: showBcc ? themeColors.primary : themeColors.text.secondary, minWidth: 0, px: 0.75, py: 0.25, fontWeight: 600 }}>
                  BCC
                </Button>
              </Box>
            }
          />

          {showCc && (
            <RecipientRow
              label="CC"
              emails={ccEmails}
              input={ccInput}
              onInputChange={setCcInput}
              onAdd={() => addEmail(ccInput, ccEmails, setCcEmails, setCcInput)}
              onRemove={e => setCcEmails(ccEmails.filter(x => x !== e))}
              onKeyDown={e => handleKeyDown(e, ccInput, ccEmails, setCcEmails, setCcInput)}
              placeholder="Add CC email"
            />
          )}

          {showBcc && (
            <RecipientRow
              label="BCC"
              emails={bccEmails}
              input={bccInput}
              onInputChange={setBccInput}
              onAdd={() => addEmail(bccInput, bccEmails, setBccEmails, setBccInput)}
              onRemove={e => setBccEmails(bccEmails.filter(x => x !== e))}
              onKeyDown={e => handleKeyDown(e, bccInput, bccEmails, setBccEmails, setBccInput)}
              placeholder="Add BCC email"
            />
          )}

          {/* Subject */}
          <Box sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${themeColors.border}` }}>
            <InputBase
              fullWidth
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject*"
              sx={{ fontSize: 13, color: themeColors.text.primary, '& input::placeholder': { color: '#bdbdbd' } }}
            />
          </Box>

          {/* Toolbar */}
          <Box sx={{ px: 1, py: 0.5, borderBottom: `1px solid ${themeColors.border}`, display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
            {TOOLBAR_FMT.map(({ label, icon, cmd }) => (
              <Tooltip key={cmd} title={label} placement="top">
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); exec(cmd) }} sx={activeToolbarBtnSx(!!activeFormats[cmd])}>{icon}</IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            {TOOLBAR_LIST.map(({ label, icon, cmd }) => (
              <Tooltip key={cmd} title={label} placement="top">
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); exec(cmd) }} sx={activeToolbarBtnSx(!!activeFormats[cmd])}>{icon}</IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            {TOOLBAR_ALIGN.map(({ label, icon, cmd }) => (
              <Tooltip key={cmd} title={label} placement="top">
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); exec(cmd) }} sx={activeToolbarBtnSx(!!activeFormats[cmd])}>{icon}</IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            {TOOLBAR_INDENT.map(({ label, icon, cmd }) => (
              <Tooltip key={cmd} title={label} placement="top">
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); exec(cmd) }} sx={toolbarBtnSx}>{icon}</IconButton>
              </Tooltip>
            ))}
            <Tooltip title="Blockquote" placement="top">
              <IconButton
                size="small"
                onMouseDown={e => {
                  e.preventDefault()
                  let isBlockquote = false
                  try {
                    const val = document.queryCommandValue('formatBlock')
                    isBlockquote = typeof val === 'string' && val.toLowerCase() === 'blockquote'
                  } catch (err) {}

                  if (isBlockquote) {
                    exec('formatBlock', 'p')
                  } else {
                    exec('formatBlock', 'blockquote')
                  }
                }}
                sx={activeToolbarBtnSx(!!activeFormats.blockquote)}
              >
                <FormatQuoteIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert Link" placement="top">
              <IconButton size="small" onMouseDown={e => {
                e.preventDefault()
                const url = prompt('Enter URL')
                if (url) {
                  const sel = window.getSelection()
                  if (sel && sel.isCollapsed) {
                    const linkHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
                    document.execCommand('insertHTML', false, linkHtml)
                    bodyRef.current?.focus()
                  } else {
                    exec('createLink', url)
                  }
                }
              }} sx={toolbarBtnSx}>
                <LinkIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Attach File" placement="top">
              <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={toolbarBtnSx}>
                <AttachFileIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleAttach} />
          </Box>

          {/* Editable body */}
          <Box
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Write email here"
            onKeyUp={updateActiveFormats}
            onMouseUp={updateActiveFormats}
            onSelect={updateActiveFormats}
            sx={{
              flex: 1,
              minHeight: 220,
              px: 2.5,
              py: 2,
              fontSize: 13,
              color: themeColors.text.primary,
              outline: 'none',
              lineHeight: 1.7,
              '&:empty::before': {
                content: 'attr(data-placeholder)',
                color: '#bdbdbd',
                pointerEvents: 'none',
                display: 'block',
              },
              '& blockquote': {
                borderLeft: `3px solid ${themeColors.primary}`,
                margin: '8px 0',
                paddingLeft: '12px',
                color: themeColors.text.secondary,
                fontStyle: 'italic',
              },
              '& a': {
                color: themeColors.text.link,
                textDecoration: 'underline',
              },
            }}
          />
        </Box>

        {/* Attachments Section (rendered at the bottom side, above the footer actions) */}
        {attachments.length > 0 && (
          <Box sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${themeColors.border}`,
            bgcolor: '#f6f5fa',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flexShrink: 0,
            maxHeight: 150,
            overflowY: 'auto',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: themeColors.text.primary }}>
                Attachments
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 18,
                height: 18,
                borderRadius: '9px',
                bgcolor: '#eae6ff',
                color: '#5c35d8',
                fontSize: 11,
                fontWeight: 700,
                px: 0.5,
              }}>
                {attachments.length}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map((file, i) => (
                <Box key={i} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#ffffff',
                  border: '1px solid #e0e5ee',
                  borderRadius: '6px',
                  pl: 1,
                  pr: 0.5,
                  py: 0.5,
                  height: 32,
                }}>
                  <FileIcon sx={{ fontSize: 16, color: '#706e6b', mr: 0.75 }} />
                  <Typography sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#181818',
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {file.name}
                  </Typography>
                  <Box sx={{ width: '1px', height: '14px', bgcolor: '#e0e5ee', mx: 1 }} />
                  <Typography sx={{
                    fontSize: 11,
                    color: '#706e6b',
                    mr: 0.5,
                    whiteSpace: 'nowrap',
                  }}>
                    {(file.size / 1024).toFixed(0)}kb
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))}
                    sx={{
                      p: 0.25,
                      color: '#706e6b',
                      '&:hover': { color: '#c23934', bgcolor: 'rgba(194, 57, 52, 0.04)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Footer ── */}
        <Box sx={{
          px: 2.5, py: 1.5,
          borderTop: `1px solid ${themeColors.border}`,
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: '#fff', flexShrink: 0, flexWrap: 'wrap',
        }}>
          {/* Pre-uploaded Files button */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<FolderIcon sx={{ fontSize: 15 }} />}
            onClick={openFilesPopover}
            sx={{
              fontSize: 12, textTransform: 'none',
              borderColor: themeColors.border, color: themeColors.text.secondary,
              borderRadius: '6px', fontWeight: 500,
              '&:hover': { borderColor: themeColors.primary, color: themeColors.primary },
            }}
          >
            Files
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            size="small"
            startIcon={isSending ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 14 }} />}
            onClick={handleSend}
            disabled={isSending || !canSend}
            sx={{
              fontSize: 13, fontWeight: 700, textTransform: 'none',
              borderRadius: '6px', bgcolor: '#4f2f86', boxShadow: 'none', px: 2.5,
              '&:hover': { bgcolor: '#3f236f', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#c5b8dc', color: '#fff' },
            }}
          >
            Send now
          </Button>
        </Box>

        {/* ── Pre-uploaded files popover ── */}
        <Popover
          open={Boolean(filesAnchor)}
          anchorEl={filesAnchor}
          onClose={closeFilesPopover}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: themeColors.shadow?.dialog ?? '0 8px 32px rgba(0,0,0,0.18)',
              border: `1px solid ${themeColors.border}`,
              width: 320,
              maxHeight: 380,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Popover header */}
          <Box sx={{
            px: 2, py: 1.5,
            borderBottom: `1px solid ${themeColors.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: themeColors.text.primary }}>
              Pre-uploaded Files
            </Typography>
          </Box>

          {/* Popover file list */}
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {preFilesLoading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} sx={{ color: themeColors.primary }} />
              </Box>
            ) : preFiles.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <FolderIcon sx={{ fontSize: 32, color: themeColors.text.muted, mb: 1 }} />
                <Typography sx={{ fontSize: 12.5, color: themeColors.text.secondary }}>
                  No files uploaded yet
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: themeColors.text.muted, mt: 0.5 }}>
                  Go to Settings → Email Files
                </Typography>
              </Box>
            ) : (
              preFiles.map((f, i) => {
                const alreadyAttached = attachments.some(a => a.name === f.fileName)
                return (
                  <Box key={f.id}>
                    {i > 0 && <Divider />}
                    <Box
                      onClick={() => !alreadyAttached && attachPreFile(f)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1.25,
                        cursor: alreadyAttached ? 'default' : 'pointer',
                        opacity: alreadyAttached ? 0.5 : 1,
                        transition: 'background 0.12s',
                        '&:hover': alreadyAttached ? {} : { bgcolor: '#f5f3ff' },
                      }}
                    >
                      <PreFileIcon type={f.contentType} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: 12.5, fontWeight: 600,
                          color: themeColors.text.primary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {f.fileName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: themeColors.text.secondary }}>
                          {formatBytes(f.sizeBytes)}
                        </Typography>
                      </Box>
                      {alreadyAttached && (
                        <Typography sx={{ fontSize: 10.5, color: themeColors.primary, fontWeight: 600, flexShrink: 0 }}>
                          Added
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              })
            )}
          </Box>
        </Popover>
      </Box>
    </Drawer>
  )
}

// ── Recipient row ──────────────────────────────────────────────────────────────

interface RecipientRowProps {
  label: string
  emails: string[]
  input: string
  onInputChange: (v: string) => void
  onAdd: () => void
  onRemove: (email: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  placeholder?: string
  actions?: React.ReactNode
}

function RecipientRow({ label, emails, input, onInputChange, onAdd, onRemove, onKeyDown, placeholder, actions }: RecipientRowProps) {
  return (
    <Box sx={{
      px: 2.5, py: 1,
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      borderBottom: `1px solid ${themeColors.border}`, minHeight: 44,
    }}>
      <Typography sx={{ fontSize: 13, color: themeColors.text.secondary, width: 56, flexShrink: 0, pt: 0.75, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', pt: 0.25 }}>
        {emails.map(email => (
          <Chip
            key={email}
            label={email}
            size="small"
            onDelete={() => onRemove(email)}
            sx={{
              fontSize: 12, height: 26,
              bgcolor: '#eef4ff', color: themeColors.text.primary,
              border: '1px solid #c5d8f5',
              '& .MuiChip-deleteIcon': { fontSize: 13, color: themeColors.text.secondary },
            }}
          />
        ))}
        <InputBase
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onAdd}
          placeholder={placeholder}
          sx={{ fontSize: 13, flex: 1, minWidth: 140, '& input::placeholder': { color: '#bdbdbd', fontSize: 13 } }}
        />
      </Box>
      {actions}
    </Box>
  )
}
